import { useEffect, useMemo, useRef } from 'react';
import useAuthStore from '../store/authStore';
import useHerdContextStore from '../store/herdContextStore';
import useUiStore from '../store/uiStore';
import { buildUiPreferencesPayload, getUiPreferenceSnapshot, stablePreferenceStringify } from '../utils/userPreferences';

function UserPreferenceSync() {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const updateProfile = useAuthStore((state) => state.updateProfile);

  const herdContext = useHerdContextStore((state) => ({
    siteId: state.siteId,
    unitId: state.unitId,
    baharbandId: state.baharbandId,
    scope: state.scope,
    denseMode: state.denseMode,
    quickSearch: state.quickSearch,
    dateContext: state.dateContext,
  }));
  const hydrateFromPreferences = useHerdContextStore((state) => state.hydrateFromPreferences);

  const batchMode = useUiStore((state) => state.batchMode);
  const setBatchMode = useUiStore((state) => state.setBatchMode);
  const setPreferencesSyncState = useUiStore((state) => state.setPreferencesSyncState);
  const setLastPreferencesSyncAt = useUiStore((state) => state.setLastPreferencesSyncAt);

  const hydratedUserRef = useRef('');
  const lastSyncedRef = useRef('');
  const saveTimerRef = useRef(null);
  const skipNextSyncRef = useRef(false);

  const currentPreferences = useMemo(() => buildUiPreferencesPayload({ herdContext, powerUser: { batchMode }, basePreferences: user?.ui_preferences || {} }), [batchMode, herdContext, user?.ui_preferences]);
  const currentSignature = useMemo(() => stablePreferenceStringify(currentPreferences), [currentPreferences]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const serverSnapshot = buildUiPreferencesPayload({ ...getUiPreferenceSnapshot(user), basePreferences: user?.ui_preferences || {} });
    const serverSignature = stablePreferenceStringify(serverSnapshot);

    if (hydratedUserRef.current !== String(user.id)) {
      hydrateFromPreferences(serverSnapshot.herd_context);
      setBatchMode(!!serverSnapshot.power_user?.batchMode);
      hydratedUserRef.current = String(user.id);
      lastSyncedRef.current = serverSignature;
      skipNextSyncRef.current = true;
      setPreferencesSyncState('synced');
      if (user?.ui_preferences && Object.keys(user.ui_preferences).length) {
        setLastPreferencesSyncAt(new Date().toISOString());
      }
      return;
    }

    if (!lastSyncedRef.current) {
      lastSyncedRef.current = serverSignature;
    }
  }, [isAuthenticated, user, hydrateFromPreferences, setBatchMode, setPreferencesSyncState, setLastPreferencesSyncAt]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined;
    if (!hydratedUserRef.current) return undefined;
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return undefined;
    }
    if (currentSignature === lastSyncedRef.current) {
      setPreferencesSyncState('synced');
      return undefined;
    }

    setPreferencesSyncState('syncing');
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const result = await updateProfile({ ui_preferences: currentPreferences });
      if (result?.success) {
        lastSyncedRef.current = currentSignature;
        setPreferencesSyncState('synced');
        setLastPreferencesSyncAt(new Date().toISOString());
      } else {
        setPreferencesSyncState('error');
      }
    }, 800);

    return () => clearTimeout(saveTimerRef.current);
  }, [currentPreferences, currentSignature, isAuthenticated, setLastPreferencesSyncAt, setPreferencesSyncState, updateProfile, user?.id]);

  return null;
}

export default UserPreferenceSync;
